import * as React from "react";
import { Text } from "@fluentui/react-components";
import type {
  FetchXmlBuilderTree,
  IFetchXmlBuilderDataSource,
  QueryEntityDefinition,
} from "./types.js";
import { EntitySelectionSection } from "./components/sections/EntitySelectionSection.js";

export interface EntitySelectionControlProps {
  dataSource: IFetchXmlBuilderDataSource | null;
  tree?: FetchXmlBuilderTree | null;
  onTreeChange?: (tree: FetchXmlBuilderTree | null) => void;
}

function createEmptyTree(rootEntityLogicalName: string): FetchXmlBuilderTree {
  return {
    rootEntityLogicalName,
    rootSelectedAttributes: [],
    joins: [],
    conditionGroups: [],
    conditions: [],
  };
}

export function EntitySelectionControl({ dataSource, tree, onTreeChange }: EntitySelectionControlProps): JSX.Element {
  const [entities, setEntities] = React.useState<QueryEntityDefinition[]>([]);
  const [entitiesLoading, setEntitiesLoading] = React.useState(false);
  const [entitiesError, setEntitiesError] = React.useState("");
  const [rootMetadataLoading, setRootMetadataLoading] = React.useState(false);
  const [rootMetadataError, setRootMetadataError] = React.useState("");

  const rootEntityLogicalName = tree?.rootEntityLogicalName || "";

  React.useEffect(() => {
    setEntities([]);
    setEntitiesError("");

    if (!dataSource) {
      return;
    }

    let disposed = false;
    setEntitiesLoading(true);

    dataSource
      .listEntities()
      .then((items) => {
        if (disposed) {
          return;
        }

        setEntities(items);
      })
      .catch((error) => {
        if (disposed) {
          return;
        }

        setEntitiesError(String(error));
      })
      .finally(() => {
        if (disposed) {
          return;
        }

        setEntitiesLoading(false);
      });

    return () => {
      disposed = true;
    };
  }, [dataSource]);

  React.useEffect(() => {
    setRootMetadataError("");
    setRootMetadataLoading(false);

    if (!dataSource || !rootEntityLogicalName) {
      return;
    }

    let disposed = false;
    setRootMetadataLoading(true);

    dataSource
      .getEntityMetadata(rootEntityLogicalName)
      .catch((error) => {
        if (disposed) {
          return;
        }

        setRootMetadataError(String(error));
      })
      .finally(() => {
        if (disposed) {
          return;
        }

        setRootMetadataLoading(false);
      });

    return () => {
      disposed = true;
    };
  }, [dataSource, rootEntityLogicalName]);

  const rootEntityOptions = React.useMemo(
    () => entities.map((item) => [item.logicalName, `${item.displayName} (${item.logicalName})`] as [string, string]),
    [entities],
  );

  const onSelectRootEntity = React.useCallback(
    (value: string) => {
      const nextRootEntityLogicalName = value.trim();
      if (!nextRootEntityLogicalName) {
        onTreeChange?.(null);
        return;
      }

      onTreeChange?.(createEmptyTree(nextRootEntityLogicalName));
    },
    [onTreeChange],
  );

  const onClearRootEntity = React.useCallback(() => {
    onTreeChange?.(null);
  }, [onTreeChange]);

  if (!dataSource) {
    return <Text>No datasource configured yet.</Text>;
  }

  return (
    <EntitySelectionSection
      normalizedBaseUrl={dataSource.getConnectionLabel?.() || "(datasource connected)"}
      entitiesLoading={entitiesLoading}
      entitiesError={entitiesError}
      rootEntityOptions={rootEntityOptions}
      rootEntityLogicalName={rootEntityLogicalName}
      onSelectRootEntity={onSelectRootEntity}
      onClearRootEntity={onClearRootEntity}
      rootMetadataLoading={rootMetadataLoading}
      rootMetadataError={rootMetadataError}
    />
  );
}
